import { createMarketingDashboard } from './site_marketing_dashboard'
import { createBoard } from './marketing_preview/board'
import { revealWorkspace } from './marketing_preview/entrance'
import { createFollowing } from './marketing_preview/following'
import { createInbox } from './marketing_preview/inbox'
import { createPortfolio } from './marketing_preview/portfolio'
import { createSearch } from './marketing_preview/search'
import { createWorkspace, type PreviewActions } from './marketing_preview/workspace'

function initializeMarketingPreview(): void {
  const root = document.querySelector<HTMLElement>('[data-marketing-home]')
  const mount = root?.querySelector<HTMLElement>('[data-marketing-mount]')
  if (!root || !mount) return
  const workspace = mount

  const stage = workspace.querySelector<HTMLElement>('[data-marketing-stage]')!
  const fade = stage.querySelector<HTMLElement>('[data-marketing-preview-fade]')!
  const body = workspace.querySelector<HTMLElement>('[data-marketing-body]')!
  const dashboard = workspace.querySelector<HTMLElement>('[data-dashboard-preview]')!
  const closeButton = workspace.querySelector<HTMLButtonElement>('[data-marketing-close]')!
  const sidebar = workspace.querySelector<HTMLElement>('[data-marketing-sidebar]')!
  const pages = workspace.querySelectorAll<HTMLElement>('[data-marketing-page]')
  const state = createWorkspace()
  let expanded = false
  let returnFocus: Element | null = null
  let inertSiblings: Element[] = []

  const actions: PreviewActions = {
    changed() {
      views.forEach((view) => view.render())
      dashboardController.updateSaved()
    },
    report(message) {
      workspace.querySelector('[data-marketing-status]')!.textContent = message
    },
    show(page) {
      pages.forEach((element) => {
        element.hidden = element.dataset.marketingPage !== page
      })
      sidebar
        .querySelectorAll<HTMLElement>('[data-marketing-view]')
        .forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.marketingView === page)))
    }
  }
  const dashboardController = createMarketingDashboard(dashboard, workspace, () => state.jobs)
  const views = [
    createSearch(workspace, state, actions),
    createFollowing(workspace, state, actions),
    createBoard(workspace, state, actions),
    createInbox(workspace, state, actions),
    createPortfolio(workspace, actions)
  ]

  function sizePreview(): void {
    const width = stage.clientWidth
    if (!width) return
    const scale = Math.min(1, Math.max(width / body.offsetWidth, innerWidth < 768 ? 0.5 : 0.75))
    const height = body.offsetHeight * scale
    body.style.transform = `scale(${scale})`
    stage.style.height = `${expanded ? Math.min(height, innerHeight - 34) : height}px`
    stage.classList.remove('min-h-[410px]', 'md:min-h-[615px]')
    fade.hidden = body.offsetWidth * scale <= width
  }

  function expand(): void {
    expanded = !expanded
    if (expanded) {
      if (workspace.inert) {
        workspace.getAnimations().forEach((animation) => animation.finish())
        workspace.inert = false
      }
      returnFocus = document.activeElement
      for (let node: Element = workspace; node.parentElement && node !== document.body; node = node.parentElement) {
        Array.from(node.parentElement.children)
          .filter((sibling) => sibling !== node && !sibling.hasAttribute('inert'))
          .forEach((sibling) => {
            sibling.setAttribute('inert', '')
            inertSiblings.push(sibling)
          })
      }
      workspace.setAttribute('role', 'dialog')
      workspace.setAttribute('aria-modal', 'true')
      workspace.setAttribute('aria-label', 'Refine workspace')
    } else {
      inertSiblings.forEach((sibling) => sibling.removeAttribute('inert'))
      inertSiblings = []
      workspace.removeAttribute('role')
      workspace.removeAttribute('aria-modal')
      workspace.removeAttribute('aria-label')
    }
    document.body.classList.toggle('overflow-hidden', expanded)
    for (const name of ['fixed', 'inset-4', 'z-[1000]', 'shadow-mid', 'overflow-hidden'])
      workspace.classList.toggle(name, expanded)
    closeButton.hidden = !expanded
    sizePreview()
    if (expanded) closeButton.focus()
    else if (returnFocus instanceof HTMLElement && returnFocus.isConnected) returnFocus.focus()
  }

  workspace.addEventListener('click', (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>('button')
    if (!button) return
    if (button.dataset.marketingView) actions.show(button.dataset.marketingView)
    if (dashboard.contains(button)) dashboardController.handleClick(button)
  })
  root
    .querySelectorAll<HTMLAnchorElement>('[data-marketing-story-tour]')
    .forEach((link) => link.addEventListener('click', () => actions.show(link.dataset.marketingStoryTour!)))
  root
    .querySelectorAll<HTMLButtonElement>('[data-marketing-open-demo]')
    .forEach((button) => button.addEventListener('click', expand))
  closeButton.addEventListener('click', expand)
  document.addEventListener('keydown', (event) => {
    if (expanded && event.key === 'Escape' && !event.defaultPrevented) {
      event.preventDefault()
      expand()
    }
  })
  actions.changed()
  actions.show('dashboard')
  sizePreview()
  revealWorkspace(root, workspace)
  new ResizeObserver(sizePreview).observe(stage)
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeMarketingPreview)
else initializeMarketingPreview()
